import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";

const pill = "px-2.5 py-1 border font-mono text-[9px] tracking-[0.16em] uppercase rounded-sm transition-colors";

/** Super admin: 'Show test accounts' toggle + one-time 'Z-' backfill (preview → Confirm) with before/after counts. */
export function TestAccountsBar({ show, setShow, onChanged }) {
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => reApi.get("/test-accounts/preview").then(setP).catch(() => {});
  useEffect(() => { load(); }, []);
  const pending = (p?.candidates || []).filter((c) => !c.is_test);
  const confirm = async () => {
    setBusy(true);
    try {
      const d = await reApi.post("/test-accounts/confirm", { resident_ids: pending.map((c) => c.id) });
      setResult(d); toast.success(`${d.flagged.length} resident${d.flagged.length === 1 ? "" : "s"} marked as test accounts`); load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="px-5 py-2 border-b border-white/10 space-y-2" data-testid="test-accounts-bar">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-1.5 text-[11px] text-nex-muted"><input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} data-testid="show-test-accounts" className="accent-[#d4a017]" /> Show test accounts</label>
        {pending.length > 0 && <button type="button" onClick={() => setOpen(!open)} data-testid="test-backfill-review" className={`${pill} border-amber-400/60 text-amber-200 hover:bg-amber-500/10`}>{pending.length} likely test accounts · Review</button>}
      </div>
      {open && pending.length > 0 && (
        <div className="p-3 border border-amber-400/30 rounded-sm space-y-2" data-testid="test-backfill-preview">
          <p className="text-[11px] text-nex-muted">Residents whose name starts with “Z-”. Confirm to mark them as test accounts — their rows stay, but they stop counting in every total.</p>
          <ul className="text-xs space-y-1">{pending.map((c) => <li key={c.id} data-testid={`test-backfill-row-${c.id}`} className="flex gap-3"><span className="text-nex-silver flex-1">{c.name}</span><span className="text-nex-muted">{c.status}</span><span className="text-nex-muted">{c.ledger_rows} ledger rows</span></li>)}</ul>
          <button type="button" disabled={busy} onClick={confirm} data-testid="test-backfill-confirm" className={`${pill} border-nex-gold/60 text-nex-gold hover:bg-nex-gold/10 disabled:opacity-40`}>Confirm · mark {pending.length} as test</button>
        </div>
      )}
      {result && (
        <table className="text-[11px] w-full max-w-md" data-testid="test-backfill-result">
          <thead><tr className="text-nex-muted"><th className="text-left font-normal">Count</th><th className="text-right font-normal">Before</th><th className="text-right font-normal">After</th></tr></thead>
          <tbody>{Object.keys(result.before).map((k) => <tr key={k}><td className="text-nex-silver">{k}</td><td className="text-right">{result.before[k]}</td><td className="text-right text-nex-gold">{result.after[k] ?? 0}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

/** Banner on every card of a test resident's file + the super-admin 'Test account' switch. */
export function TestAccountBanner({ resident, isSuper, onChanged }) {
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    setBusy(true);
    try { await reApi.patch(`/test-accounts/${resident.id}`, { is_test: !resident.is_test }); toast.success(resident.is_test ? "No longer a test account" : "Marked as a test account"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!resident.is_test && !isSuper) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3" data-testid="test-account-strip">
      {resident.is_test && <div data-testid="test-account-banner" className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-fuchsia-400/50 bg-fuchsia-500/10 text-fuchsia-200 text-[11px] rounded-sm"><FlaskConical className="h-3 w-3" /> Test account - not counted in any totals</div>}
      {isSuper && <label className="inline-flex items-center gap-1.5 text-[11px] text-nex-muted"><input type="checkbox" checked={!!resident.is_test} disabled={busy} onChange={flip} data-testid="test-account-switch" className="accent-[#d4a017]" /> Test account</label>}
    </div>
  );
}

export const TestBadge = () => <span data-testid="test-badge" className="ml-1.5 px-1 py-px border border-fuchsia-400/50 text-fuchsia-200 font-mono text-[8px] uppercase rounded-sm">Test</span>;
